import { makeStyles } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import AddIcon from '@material-ui/icons/Add';
import moment from 'moment/moment';
import React, { useState } from 'react';
import {
    BooleanInput,
    CreateResult,
    Identifier,
    Record,
    ReduxState,
    required,
    TextInput,
    UpdateResult,
    useCreate,
    useMutation,
    useNotify,
    useRefresh,
    useTranslate,
    useUpdate,
} from 'react-admin';
import { Form } from 'react-final-form';
import { useSelector } from 'react-redux';
import DialogTemplate from '../../../../commons/Dialog/DialogTemplate/DialogTemplate';
import DialogTemplateButtons from '../../../../commons/Dialog/DialogTemplateButtons/DialogTemplateButtons';
import ReferenceInput from '../../../../commons/ra/input/ReferenceInput';
import useCreateLanguage from '../../../../hooks/useCreateLanguages';
import { incidentTypes } from '../../../../pages/paths';
import {
    GROUP_METADATA_AVATAR_TYPES,
    IMAGES,
    LANGUAGES,
    MEDIA_LINKS_FOR_CREATE,
    METADATA_AVATAR_TYPE_PROPERTIES,
    METADATA_AVATAR_TYPES,
} from '../../../../providers/resources';
import { CustomReducerState } from '../../../../redux/reducers';
import {
    GROUP_METADATA_AVATAR_TYPE_EVENT,
    TAG_TICKETING_LOGO,
    TICKETING_ALLOWED_MULTIPLE_EVENTS_METADATA_AVATAR_TYPE_FIELD,
    TICKETING_WHEN_MULTIPLE_EVENTS_TITLE_SHOULD_BE_OVERWRITE_METADATA_AVATAR_TYPE_FIELD,
} from '../../../../utils/CONST';
import extractIdFromURI, { getURIFROMAnotherURI, getUriFromItems } from '../../../../utils/id';
import { saveMedia } from '../../../../utils/medias';
import {
    ASK_CODE_PIN_FIELD,
    ASK_INCIDENT_CREATOR_EMAIL_FIELD,
    CATEGORY_FIELD,
    CUSTOM_FIELDS_ICON_FILE_SYSTEM_MAPPER,
    CUSTOM_FIELDS_ICON_SVG_COLLECTION,
    FORCE_IMAGE_FIELD,
    FORCE_TEXT_FIELD,
    ICON_FIELD,
    ICON_SVG_COLLECTION_FIELD,
    IMAGE_FIELD,
    NAME_FIELD,
    NO_LOGGED_USERS_CAN_SEE_CREATED_EVENTS_FIELD,
    NO_LOGGED_USERS_CAN_SEE_HISTORY_FIELD,
    REPEATABLE_FIELD,
    SVG_ICON_SYSTEM,
    TICKETING_FILESYSTEM_MAPPER_ENABLED_FIELD,
    WHEN_REPEATABLE_TITLE_SHOULD_BE_OVERWRITE_FIELD,
} from '../../../../utils/metadataAvatarType';
import {
    EVENT_TYPE_CUSTOM_PROCESS_FORCE_COMMENT,
    EVENT_TYPE_CUSTOM_PROCESS_FORCE_IMAGE,
    eventTypeCustomProcessForceImageLanguages,
    eventTypeCustomProcessForceTextLanguages,
} from '../../../../utils/properties/metadataAvatarTypeProperties';
import { Language, Media, MetadataAvatarType, User } from '../../../../utils/types';
import {
    getCodeFromViolationMessage,
    getViolations,
    ValidationError,
    ViolationResolved,
} from '../../../../utils/validations/parseValidations';
import AutoCompleteInputEvent from './AutocompleteInputEvent';
import BooleanInputEventRepeatable from '../../../../commons/inputs/BooleanInputEventRepeatable';
import ImageIconSelect from '../../../image-icon-select/image-icon-select';

const collection = process.env.REACT_APP_ICONS_COLLECTION;

interface CreateFormProps {
    open: boolean;
    fromExternalInput?: boolean;
    handleClose: () => void;
    initialName?: string;
    handleCreateOnSuccess?: (data: Record) => void;
}

const useStyles = makeStyles({
    fullWidth: {
        width: '100%',
    },
});

const CreateForm = ({ open, handleClose, fromExternalInput, initialName, handleCreateOnSuccess }: CreateFormProps) => {
    const translate = useTranslate();
    const classes = useStyles();
    const notify = useNotify();
    const refresh = useRefresh();
    const [create, { loading: loadingCreate }] = useCreate();
    const [update, { loading: loadingUpdate }] = useUpdate();
    const userConnected: User | Record | null = useSelector(
        (state: CustomReducerState) => state.ticketing.user.userConnected
    );

    const [createMetadataAvatarType, { loading }] = useMutation({
        resource: METADATA_AVATAR_TYPES,
        type: 'create',
        payload: { data: {} },
    });

    const [createMetadataAvatarTypeProperty, { loading: loadingCreateMetadataAvatarTypeProperty }] = useMutation({
        resource: METADATA_AVATAR_TYPE_PROPERTIES,
        type: 'create',
        payload: { data: {} },
    });

    const [savingMedia, setSavingMedia] = useState(false);

    const [updateMetadataAvatarType, { loading: updatingAvatarType }] = useUpdate(METADATA_AVATAR_TYPES);

    const tokenWithRoles: string | undefined = useSelector(
        (state: CustomReducerState) => state.ticketing.user.tokenData?.tokenWithRoles
    );

    /**Create and get languages*/
    for (const key of Object.keys(eventTypeCustomProcessForceTextLanguages)) {
        // @ts-ignore
        useCreateLanguage(key, eventTypeCustomProcessForceTextLanguages[key]);
    }
    for (const key of Object.keys(eventTypeCustomProcessForceImageLanguages)) {
        // @ts-ignore
        useCreateLanguage(key, eventTypeCustomProcessForceImageLanguages[key]);
    }

    /**Get properties from state*/
    const getLanguagesFromState = (propertyLanguages: { en: string; fr: string; es: string }) => {
        const languagesData = useSelector((state: ReduxState) => state.admin.resources[LANGUAGES].data);
        return Object.values(languagesData)
            .map((item: Record) => {
                for (const key of Object.keys(propertyLanguages)) {
                    if (
                        (item as Language).twoLetters.toLowerCase().trim() === key.toLowerCase().trim() &&
                        (item as Language).value.toLowerCase().trim() ===
                            // @ts-ignore
                            propertyLanguages[key].toLowerCase().trim()
                    ) {
                        return (item as Language).id;
                    }
                }
            })
            .filter((item) => item !== undefined);
    };

    const eventTypeCustomProcessForceImageLanguagesInState = getLanguagesFromState(
        eventTypeCustomProcessForceImageLanguages
    );

    const eventTypeCustomProcessForceTextLanguagesInState = getLanguagesFromState(
        eventTypeCustomProcessForceTextLanguages
    );

    const resolveImageAndUpdateResource = async (
        fileData: string,
        fileName: string,
        metadataAvatarType: MetadataAvatarType
    ) => {
        if (!tokenWithRoles || !metadataAvatarType) return;
        setSavingMedia(true);

        const metadataAvatarTypeCompanyId =
            typeof metadataAvatarType.company === 'object' ? metadataAvatarType.company.id : metadataAvatarType.company;
        const resourceId = extractIdFromURI(metadataAvatarType.id as string);
        const imageAdded: Media | undefined = resourceId
            ? await saveMedia(fileData, fileName, metadataAvatarTypeCompanyId as string, resourceId, tokenWithRoles, [
                  TAG_TICKETING_LOGO,
              ])
            : undefined;

        setSavingMedia(false);

        if (imageAdded && imageAdded.id) {
            const imageUri = `${getURIFROMAnotherURI(metadataAvatarType.id as string)}/${IMAGES}/${imageAdded.id}`;
            updateMetadataAvatarType(
                METADATA_AVATAR_TYPES,
                metadataAvatarType.id as string,
                {
                    images: [...getUriFromItems(metadataAvatarType.images), imageUri],
                },
                metadataAvatarType,
                {
                    onSuccess: (response: UpdateResult) => {
                        const data = response && 'data' in response ? response.data : undefined;
                        data && handleCreateOnSuccess && handleCreateOnSuccess(data);

                        if (!fromExternalInput) {
                            handleClose();
                            refresh();
                        }
                    },
                    onFailure: () => {
                        notify('app.incidentTypes.edit.error', 'warning');
                        console.error('ERROR editing', METADATA_AVATAR_TYPES, 'adding media ', imageAdded?.id);
                    },
                }
            );
        } else {
            console.error('The image can not be added, check the token send in the request');
            notify('app.messages.imageNotAdded');
        }
    };

    const handleCreateIconMedia = (data: any, onSuccessCallback: any) => {
        create(MEDIA_LINKS_FOR_CREATE, data, {
            onSuccess: ({ data }: CreateResult) => {
                onSuccessCallback(data);
            },
            onFailure: (error) => {
                console.log('error', error);
                notify('app.subCategory.create.errors.iconNotAdded', 'error');
            },
        });
    };

    const handleUpdateMetadataAvatarType = (
        metadataAvatarTypeId: string,
        data: any,
        previousData: MetadataAvatarType,
        onSuccessCallback: any
    ) => {
        update(
            METADATA_AVATAR_TYPES,
            metadataAvatarTypeId,
            data,
            {},
            {
                onSuccess: () => {
                    onSuccessCallback();
                },
                onFailure: (error) => {
                    console.log('error', error);
                    notify('app.subCategory.create.errors.subcategoryNotUpdated', 'error');
                },
            }
        );
    };

    const handleSubmit = (formValues: {
        [NAME_FIELD]: string;
        [REPEATABLE_FIELD]: string;
        [WHEN_REPEATABLE_TITLE_SHOULD_BE_OVERWRITE_FIELD]: boolean;
        [IMAGE_FIELD]: { fileData: string; fileName: string; cropping?: boolean };
        [CATEGORY_FIELD]: string;
        [FORCE_IMAGE_FIELD]: boolean;
        [FORCE_TEXT_FIELD]: boolean;
        [TICKETING_FILESYSTEM_MAPPER_ENABLED_FIELD]: boolean;
        [ICON_SVG_COLLECTION_FIELD]: boolean;
        [NO_LOGGED_USERS_CAN_SEE_HISTORY_FIELD]: boolean;
        [ASK_INCIDENT_CREATOR_EMAIL_FIELD]: boolean;
        [NO_LOGGED_USERS_CAN_SEE_CREATED_EVENTS_FIELD]: boolean;
        [ASK_CODE_PIN_FIELD]: boolean;
        [ICON_FIELD]: string;
    }) => {
        const currentIconNameValue = ICON_FIELD in formValues ? formValues[ICON_FIELD] : null;

        createMetadataAvatarType(
            {
                payload: {
                    data: {
                        name: formValues[NAME_FIELD],
                        groupMetadataAvatarType: formValues[CATEGORY_FIELD],
                        alphaId: moment().toString(),
                        customFields: {
                            [TICKETING_ALLOWED_MULTIPLE_EVENTS_METADATA_AVATAR_TYPE_FIELD]: formValues[REPEATABLE_FIELD]
                                ? '*'
                                : 1,
                            [CUSTOM_FIELDS_ICON_SVG_COLLECTION]: formValues[ICON_SVG_COLLECTION_FIELD],
                            [CUSTOM_FIELDS_ICON_FILE_SYSTEM_MAPPER]: !formValues[ICON_SVG_COLLECTION_FIELD],
                            [TICKETING_WHEN_MULTIPLE_EVENTS_TITLE_SHOULD_BE_OVERWRITE_METADATA_AVATAR_TYPE_FIELD]:
                                formValues[REPEATABLE_FIELD]
                                    ? formValues[WHEN_REPEATABLE_TITLE_SHOULD_BE_OVERWRITE_FIELD]
                                    : false,
                            [NO_LOGGED_USERS_CAN_SEE_HISTORY_FIELD]: formValues[NO_LOGGED_USERS_CAN_SEE_HISTORY_FIELD],
                            [ASK_INCIDENT_CREATOR_EMAIL_FIELD]: formValues[ASK_INCIDENT_CREATOR_EMAIL_FIELD],
                            [NO_LOGGED_USERS_CAN_SEE_CREATED_EVENTS_FIELD]:
                                formValues[NO_LOGGED_USERS_CAN_SEE_CREATED_EVENTS_FIELD],
                            [ASK_CODE_PIN_FIELD]: formValues[ASK_CODE_PIN_FIELD],
                        },
                    },
                },
            },
            {
                onSuccess: async (response: CreateResult<Record>) => {
                    const data: any = response && 'data' in response ? response.data : undefined;
                    data && handleCreateOnSuccess && handleCreateOnSuccess(data);

                    notify('app.incidentTypes.create.notification', 'success');

                    // add property force text
                    addMetadataAvatarTypeProperty((data as MetadataAvatarType).id as string, {
                        name: EVENT_TYPE_CUSTOM_PROCESS_FORCE_COMMENT,
                        values: [formValues[FORCE_TEXT_FIELD] ? 'true' : 'false'],
                        fieldType: 'boolean',
                        languages: eventTypeCustomProcessForceTextLanguagesInState,
                    });
                    // add property force image
                    addMetadataAvatarTypeProperty((data as MetadataAvatarType).id as string, {
                        name: EVENT_TYPE_CUSTOM_PROCESS_FORCE_IMAGE,
                        values: [formValues[FORCE_IMAGE_FIELD] ? 'true' : 'false'],
                        fieldType: 'boolean',
                        languages: eventTypeCustomProcessForceImageLanguagesInState,
                    });

                    if (formValues[ICON_SVG_COLLECTION_FIELD]) {
                        if (currentIconNameValue) {
                            handleCreateIconMedia(
                                JSON.stringify({
                                    fileType: 'svg',
                                    name: currentIconNameValue.split('.')[0],
                                    customFields: {
                                        collection,
                                        type: TAG_TICKETING_LOGO,
                                    },
                                    destinationCompany: userConnected?.company['@id'],
                                    source: SVG_ICON_SYSTEM,
                                }),
                                (createdIcon: any) => {
                                    handleUpdateMetadataAvatarType(
                                        data.id,
                                        {
                                            medias: [...getUriFromItems(data.medias), createdIcon['@id']],
                                        },
                                        data,
                                        () => {
                                            if (!fromExternalInput) {
                                                finishSubmit();
                                            }
                                        }
                                    );
                                }
                            );
                        } else {
                            if (!fromExternalInput) {
                                finishSubmit();
                            }
                        }
                    } else {
                        if (!formValues[IMAGE_FIELD]) {
                            if (!fromExternalInput) {
                                finishSubmit();
                            }
                        } else {
                            data &&
                                (await resolveImageAndUpdateResource(
                                    formValues[IMAGE_FIELD].fileData,
                                    formValues[IMAGE_FIELD].fileName,
                                    data as MetadataAvatarType
                                ));
                            finishSubmit();
                        }
                    }
                },
                onFailure: (...args: unknown[]) => {
                    const violations: ViolationResolved[] = getViolations(args[0] as ValidationError);
                    if (violations && violations.length > 0) {
                        const violation = violations[0];
                        notify(`${getCodeFromViolationMessage(violation?.message)}`, 'warning');
                    } else {
                        notify('app.incidentTypes.create.error', 'warning');
                    }
                },
            }
        );
    };

    const finishSubmit = () => {
        handleClose();
        refresh();
    };

    const addMetadataAvatarTypeProperty = (
        metadataAvatarTypeId: string,
        data: { name: string; fieldType: string; values: any[]; languages: (Identifier | undefined)[] }
    ) => {
        createMetadataAvatarTypeProperty(
            {
                payload: {
                    data: {
                        metadataAvatarType: metadataAvatarTypeId,
                        name: data.name,
                        values: data.values,
                        fieldType: data.fieldType,
                        languages: data.languages,
                        private: false,
                        option: 'pre_filled',
                    },
                },
            },
            {
                // onSuccess: (response: CreateResult) => {
                //     //
                // },
                onFailure: (error: any) => {
                    console.error('CREATING METADATA AVATAR TYPE PROPERTY', error);
                    notify('app.messages.createForceMetadataAvatarTypePropertyError', 'error', {
                        propertyName:
                            data.name === EVENT_TYPE_CUSTOM_PROCESS_FORCE_IMAGE
                                ? translate('app.incidentTypes.create.input.forceImage')
                                : translate('app.incidentTypes.create.input.forceText'),
                    });
                },
            }
        );
    };

    return (
        <DialogTemplate
            title={translate('app.incidentTypes.create.title')}
            open={open}
            onClose={handleClose}
            icon={<AddIcon />}
            maxWidth={'md'}
        >
            <Form
                onSubmit={handleSubmit}
                initialValues={{
                    [NAME_FIELD]: initialName ? initialName : null,
                    [ICON_SVG_COLLECTION_FIELD]: true,
                    [NO_LOGGED_USERS_CAN_SEE_HISTORY_FIELD]: true,
                    [NO_LOGGED_USERS_CAN_SEE_CREATED_EVENTS_FIELD]: true,
                    [ASK_INCIDENT_CREATOR_EMAIL_FIELD]: true,
                    [ASK_CODE_PIN_FIELD]: true,
                }}
                render={({ handleSubmit, valid, pristine, values, form: { change } }) => {
                    return (
                        <Grid container>
                            <form onSubmit={handleSubmit} className={classes.fullWidth}>
                                <Grid item xs>
                                    <TextInput
                                        autoFocus
                                        variant={'standard'}
                                        required
                                        key={NAME_FIELD}
                                        fullWidth
                                        type={'text'}
                                        label={translate('app.incidentTypes.create.input.name')}
                                        source={NAME_FIELD}
                                    />
                                </Grid>
                                <Grid item>
                                    <ReferenceInput
                                        basePath={incidentTypes}
                                        reference={GROUP_METADATA_AVATAR_TYPES}
                                        filter={{
                                            alias: GROUP_METADATA_AVATAR_TYPE_EVENT,
                                            hidden: false,
                                            order__createdAt: 'DESC',
                                        }}
                                        isRequired={true}
                                        filterToQuery={(searchText: string) => ({
                                            name: searchText,
                                        })}
                                        source={CATEGORY_FIELD}
                                        label={translate('app.incidentTypes.create.input.selectEvent')}
                                        validate={[required()]}
                                    >
                                        {/*<AutocompleteInput*/}
                                        {/*    create={<CreateFromAutocompleteInput />}*/}
                                        {/*    optionValue={'@id'}*/}
                                        {/*    optionText={'name'}*/}
                                        {/*    fullWidth*/}
                                        {/*    variant={'standard'}*/}
                                        {/*/>*/}
                                        <AutoCompleteInputEvent />
                                    </ReferenceInput>
                                </Grid>

                                <BooleanInputEventRepeatable />

                                <Grid item>
                                    <BooleanInput
                                        label={
                                            <Typography variant={'subtitle2'} style={{ fontWeight: 'bold' }}>
                                                {translate('app.incidentTypes.create.input.forceText')}
                                            </Typography>
                                        }
                                        source={FORCE_TEXT_FIELD}
                                    />
                                </Grid>

                                <Grid item>
                                    <BooleanInput
                                        label={
                                            <Typography variant={'subtitle2'} style={{ fontWeight: 'bold' }}>
                                                {translate('app.incidentTypes.create.input.forceImage')}
                                            </Typography>
                                        }
                                        source={FORCE_IMAGE_FIELD}
                                    />
                                </Grid>

                                <Grid item>
                                    <BooleanInput
                                        label={
                                            <Typography variant={'subtitle2'} style={{ fontWeight: 'bold' }}>
                                                {translate(
                                                    'app.incidentTypes.create.input.noLoggerUserCanCreateEvents'
                                                )}
                                            </Typography>
                                        }
                                        source={NO_LOGGED_USERS_CAN_SEE_CREATED_EVENTS_FIELD}
                                    />
                                </Grid>

                                <Grid item>
                                    <BooleanInput
                                        label={
                                            <Typography variant={'subtitle2'} style={{ fontWeight: 'bold' }}>
                                                {translate('app.incidentTypes.create.input.noLoggerUserCanSeeHistory')}
                                            </Typography>
                                        }
                                        source={NO_LOGGED_USERS_CAN_SEE_HISTORY_FIELD}
                                    />
                                </Grid>

                                <Grid item>
                                    <BooleanInput
                                        label={
                                            <Typography variant={'subtitle2'} style={{ fontWeight: 'bold' }}>
                                                {translate('app.incidentTypes.create.input.askIncidentCreatorEmail')}
                                            </Typography>
                                        }
                                        source={ASK_INCIDENT_CREATOR_EMAIL_FIELD}
                                    />
                                </Grid>

                                <Grid item>
                                    <BooleanInput
                                        label={
                                            <Typography variant={'subtitle2'} style={{ fontWeight: 'bold' }}>
                                                {translate('app.incidentTypes.create.input.askIncidentCodePin')}
                                            </Typography>
                                        }
                                        source={ASK_CODE_PIN_FIELD}
                                    />
                                </Grid>

                                <ImageIconSelect
                                    values={values}
                                    iconOrImageActive={values[ICON_SVG_COLLECTION_FIELD]}
                                    changeIconType={change}
                                    name={IMAGE_FIELD}
                                    accept='image/*'
                                />

                                <DialogTemplateButtons
                                    outside
                                    withCancel
                                    onCancelClick={handleClose}
                                    withCreate
                                    isSubmitting={loading || savingMedia || updatingAvatarType}
                                    isValidForm={valid && !pristine && !values[IMAGE_FIELD]?.cropping}
                                    cancelButtonLabel={translate('app.cancel')}
                                    createButtonLabel={translate('app.valid')}
                                />
                            </form>
                        </Grid>
                    );
                }}
            />
        </DialogTemplate>
    );
};

export default CreateForm;
